import { useState, useRef, useMemo, useCallback, useEffect } from 'react';

/**
 * Gets current time using performance.now() with fallback to Date.now()
 * @returns {number} Current timestamp
 */
const getNow = () => {
  if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
    return performance.now();
  }
  return Date.now();
};

/**
 * Custom smooth scroll with fast animation
 * @param {number} targetY - Target scroll position
 * @param {number} duration - Animation duration in ms (default: 100ms for snappy response)
 */
const smoothScrollTo = (targetY, duration = 100) => {
  if (typeof window === 'undefined') {
    return;
  }

  const startY = window.scrollY || window.pageYOffset || 0;
  const difference = targetY - startY;
  const startTime = getNow();

  // Ease-out cubic for snappy feel
  const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

  const animateScroll = (currentTime) => {
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = easeOutCubic(progress);

    window.scrollTo(0, startY + difference * easedProgress);

    if (progress < 1) {
      requestAnimationFrame(animateScroll);
    }
  };

  requestAnimationFrame(animateScroll);
};

/**
 * Hook for managing section navigation with scroll spy functionality
 * @param {Array} sectionIds - Array of section IDs to track
 * @param {Array} sidebarMenuItemsBase - Base sidebar menu items configuration
 * @param {Array} mobileMenuItemsBase - Base mobile menu items configuration
 * @returns {Object} Navigation state and handlers
 */
export const useSectionNavigation = (sectionIds, sidebarMenuItemsBase, mobileMenuItemsBase) => {
  const [activeMobileMenuId, setActiveMobileMenuId] = useState('summary');
  const [activeSidebarMenuId, setActiveSidebarMenuId] = useState('content');

  const isScrollSpyEnabled = useRef(true);
  const scrollEndTimeoutRef = useRef(null);
  const lastScrollPositionRef = useRef(0);
  const sectionOffsetsRef = useRef({});
  const resizeObserversRef = useRef([]);

  /**
   * Cleanup resize observers
   */
  const cleanupResizeObservers = useCallback(() => {
    resizeObserversRef.current.forEach((observer) => observer.disconnect());
    resizeObserversRef.current = [];
  }, []);

  /**
   * Compute and cache section offsets
   */
  const computeSectionOffsets = useCallback(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    const offsets = {};

    sectionIds.forEach((sectionId) => {
      const element = document.getElementById(sectionId);
      if (element) {
        offsets[sectionId] = element.getBoundingClientRect().top + window.scrollY;
      }
    });

    sectionOffsetsRef.current = offsets;
  }, [sectionIds]);

  /**
   * Scroll to a specific section
   */
  const scrollToSection = useCallback((sectionId) => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    let targetPosition = sectionOffsetsRef.current[sectionId];

    if (typeof targetPosition !== 'number') {
      const element = document.getElementById(sectionId);
      if (!element) {
        return;
      }
      targetPosition = element.getBoundingClientRect().top + window.scrollY;
    }

    const offset = 120;
    smoothScrollTo(targetPosition - offset);
  }, []);

  /**
   * Handle mobile menu item click
   */
  const handleMobileMenuClick = useCallback((itemId) => {
    // Disable scroll spy during manual navigation
    isScrollSpyEnabled.current = false;

    // Clear any existing timeout
    if (scrollEndTimeoutRef.current) {
      clearTimeout(scrollEndTimeoutRef.current);
    }

    // Scroll immediately before state updates to avoid render delay
    scrollToSection(itemId);

    setActiveMobileMenuId(itemId);
    // Keep sidebar in sync (if item exists in sidebar)
    if (sidebarMenuItemsBase.some((item) => item.id === itemId)) {
      setActiveSidebarMenuId(itemId);
    }
  }, [scrollToSection, sidebarMenuItemsBase]);

  /**
   * Handle sidebar menu item click
   */
  const handleSidebarMenuClick = useCallback((itemId) => {
    // Disable scroll spy during manual navigation
    isScrollSpyEnabled.current = false;

    // Clear any existing timeout
    if (scrollEndTimeoutRef.current) {
      clearTimeout(scrollEndTimeoutRef.current);
    }

    // Scroll immediately before state updates to avoid render delay
    scrollToSection(itemId);

    setActiveSidebarMenuId(itemId);
    setActiveMobileMenuId(itemId); // Keep mobile menu in sync
  }, [scrollToSection]);

  /**
   * Build mobile menu items with dynamic active state and onClick
   */
  const mobileMenuItems = useMemo(
    () =>
      mobileMenuItemsBase.map((item) => ({
        ...item,
        isActive: item.id === activeMobileMenuId,
        onClick: () => handleMobileMenuClick(item.id),
      })),
    [activeMobileMenuId, handleMobileMenuClick, mobileMenuItemsBase]
  );

  /**
   * Build sidebar menu items with dynamic active state and onClick
   */
  const sidebarMenuItems = useMemo(
    () =>
      sidebarMenuItemsBase.map((item) => ({
        ...item,
        isActive: item.id === activeSidebarMenuId,
        onClick: () => handleSidebarMenuClick(item.id),
      })),
    [activeSidebarMenuId, handleSidebarMenuClick, sidebarMenuItemsBase]
  );

  /**
   * Compute section offsets on mount/resizes
   */
  useEffect(() => {
    computeSectionOffsets();

    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleResize = () => computeSectionOffsets();
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, [computeSectionOffsets]);

  /**
   * Keep offsets in sync when sections change height (ResizeObserver fallback to scroll)
   */
  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const ResizeObserverClass = window.ResizeObserver;

    if (typeof ResizeObserverClass !== 'function') {
      const handleScroll = () => computeSectionOffsets();
      window.addEventListener('scroll', handleScroll);
      return () => {
        window.removeEventListener('scroll', handleScroll);
      };
    }

    cleanupResizeObservers();

    const observers = sectionIds
      .map((sectionId) => {
        const element = document.getElementById(sectionId);
        if (!element) {
          return null;
        }
        const observer = new ResizeObserverClass(() => computeSectionOffsets());
        observer.observe(element);
        return observer;
      })
      .filter(Boolean);

    resizeObserversRef.current = observers;

    return () => {
      cleanupResizeObservers();
    };
  }, [sectionIds, computeSectionOffsets, cleanupResizeObservers]);

  /**
   * Scroll spy - update active menu item based on scroll position
   */
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollPosition = window.scrollY;

      // If scroll spy is disabled (manual navigation), detect when scroll stops
      if (!isScrollSpyEnabled.current) {
        // Clear previous timeout
        if (scrollEndTimeoutRef.current) {
          clearTimeout(scrollEndTimeoutRef.current);
        }

        // Re-enable scroll spy after scroll has stopped for 150ms
        scrollEndTimeoutRef.current = setTimeout(() => {
          isScrollSpyEnabled.current = true;
        }, 150);

        lastScrollPositionRef.current = currentScrollPosition;
        return;
      }

      const scrollPosition = currentScrollPosition + 150; // offset for header

      for (const sectionId of sectionIds) {
        const element = document.getElementById(sectionId);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveMobileMenuId(sectionId);
            // Also update sidebar menu (if section exists in sidebar)
            if (sidebarMenuItemsBase.some((item) => item.id === sectionId)) {
              setActiveSidebarMenuId(sectionId);
            }
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      // Clean up timeout on unmount
      if (scrollEndTimeoutRef.current) {
        clearTimeout(scrollEndTimeoutRef.current);
      }
    };
  }, [sectionIds, sidebarMenuItemsBase]);

  return {
    activeMobileMenuId,
    activeSidebarMenuId,
    mobileMenuItems,
    sidebarMenuItems,
    scrollToSection,
  };
};

export default useSectionNavigation;

